import { Action, AnyAction, PayloadAction, createSlice } from '@reduxjs/toolkit';
import { initialState } from './initialState';
import { ApiExError, SessionType } from '@shared';
import { AppState } from './store';
import { createSimpleMiddleware } from './createSimpleMiddleware';
import {
  postAdvisorUpdatesByAdvisorIdResponse,
  putAdvisorReferralSourceByAdvisorIdResponse,
  putAdvisorOrganizationByAdvisorIdResponse,
} from './advisorModel';
import { postSeatInvitationAcceptBySubscriptionSeatIdResponse } from './subscriptionSeatsModel';
import { Dispatch } from 'react';
import { postPartnerLinkRequestUpdateStatusResponse } from './verifyPartnerLinkRequestModel';
import { fetchClientsForAdvisorByAdvisorIdResponse } from './clientsModel';
import { postAdvisorFieldUpdateByAdvisorIdResponse } from './advisorAdminPortalModel';

const sessionSlice = createSlice({
  name: 'session',
  initialState: initialState.session,
  reducers: {
    storeAuth0Session(state, { payload }: PayloadAction<Auth0SessionData>) {
      state.auth0Session = payload;
    },
    setClientSessionType(state) {
      state.sessionType = SessionType.client;
    },
    fetchSessionForAuthRequest: {
      // reducer/prepare pattern is required to allow for the optional invalidating flag
      reducer(state, action: PayloadAction<{ invalidating?: boolean }>) {
        if (action.payload.invalidating) {
          state.isInvalidating = true;
        } else {
          state.isLoading = true;
        }
      },
      prepare(payload?: { invalidating?: boolean }) {
        return { payload: payload || {} };
      },
    },
    fetchSessionForAuthResponse(state, { payload }: PayloadAction<Session>) {
      state.isLoading = false;
      state.isInvalidating = false;
      Object.assign(state, payload);
    },
    fetchSessionForAuthFailure(state, _action: PayloadAction<ApiExError>) {
      state.isLoading = false;
      state.isInvalidating = false;
      state.sessionType = SessionType.unauthenticated;
      state.advisor = null;
      state.auth0Session = null;
    },
    closeSession(state) {
      state.sessionType = SessionType.unauthenticated;
      state.advisor = null;
      state.auth0Session = null;
    },
    putAdvisorConsentByAdvisorIdRequest(state, _action: PayloadAction<PutAdvisorConsentProps>) {
      state.isLoading = true;
    },
    putAdvisorConsentByAdvisorIdResponse(state, _action: PayloadAction<AdvisorConsentsDbRecord>) {
      state.isLoading = false;
      state.sessionType = SessionType.advisor;
    },
    putAdvisorConsentByAdvisorIdFailure(state, _action: PayloadAction<ApiExError>) {
      state.isLoading = false;
    },
    putAdvisorConsentsByAdvisorIdRequest(
      state,
      _action: PayloadAction<PutAdvisorConsentsByAdvisorIdProps>,
    ) {
      state.isLoading = true;
    },
    putAdvisorConsentsByAdvisorIdResponse(
      state,
      _action: PayloadAction<{
        success: boolean;
        message: string;
        results: AdvisorConsentsDbRecord[];
      }>,
    ) {
      state.isLoading = false;
    },
    putAdvisorConsentsByAdvisorIdFailure(state, _action: PayloadAction<ApiExError>) {
      state.isLoading = false;
    },
    postGoogleAuthCodeByAdvisorIdRequest(
      state,
      _action: PayloadAction<PostOAuthCodeByAdvisorIdProps>,
    ) {
      state.oauth = {
        status: 'loading',
        error: null,
        redirectInitiated: false,
      };
    },
    postGoogleAuthCodeByAdvisorIdResponse(
      state,
      _action: PayloadAction<PostOAuthCodeByAdvisorIdProps>,
    ) {
      state.oauth.status = 'complete';
      state.oauth.error = null;
    },
    postGoogleAuthCodeByAdvisorIdFailure(state, _action: PayloadAction<ApiExError>) {
      state.oauth.status = 'error';
      state.oauth.error =
        'An error occurred while connecting your Google account. Please try again or contact support.';
    },
    postMicrosoftAuthCodeByAdvisorIdRequest(
      state,
      _action: PayloadAction<PostOAuthCodeByAdvisorIdProps>,
    ) {
      state.oauth.status = 'loading';
    },
    postMicrosoftAuthCodeByAdvisorIdResponse(
      state,
      _action: PayloadAction<PostOAuthCodeByAdvisorIdProps>,
    ) {
      state.oauth.status = 'complete';
      state.oauth.error = null;
    },
    postMicrosoftAuthCodeByAdvisorIdFailure(state, _action: PayloadAction<ApiExError>) {
      state.oauth.status = 'error';
      state.oauth.error =
        'An error occurred while connecting your Microsoft account. Please try again or contact support.';
    },
    deleteOauthConnectionByAdvisorIdRequest(
      state,
      _action: PayloadAction<{ advisorId: string; provider: string }>,
    ) {
      state.isLoading = true;
    },
    deleteOauthConnectionByAdvisorIdResponse(state) {
      state.isLoading = false;
    },
    deleteOauthConnectionByAdvisorIdFailure(state, _action: PayloadAction<ApiExError>) {
      state.isLoading = false;
    },
    postAdvisorOrganizationUpdateByAdvisorIdRequest(state, _action: PayloadAction<AdvisorProfile>) {
      state.isLoading = true;
    },
    postAdvisorOrganizationUpdateByAdvisorIdResponse(state, _action: PayloadAction<Advisor>) {
      state.isLoading = false;
    },
    postAdvisorOrganizationUpdateByAdvisorIdFailure(state, _action: PayloadAction<ApiExError>) {
      state.isLoading = false;
    },
    fetchAuth0ConnectionTypeByEmailForPublicRequest(state, { payload }: PayloadAction<string>) {
      state.preLogin.status = 'loading';
      state.preLogin.error = null;
      state.preLogin.email = payload;
    },
    fetchAuth0ConnectionTypeByEmailForPublicResponse(
      state,
      { payload }: PayloadAction<Auth0ConnectionTypeResponse>,
    ) {
      state.preLogin.status = 'complete';
      state.preLogin.connection = payload.connection;
      state.preLogin.email = payload.email;
      state.preLogin.phoneNumber = payload.phoneNumber;
    },
    fetchAuth0ConnectionTypeByEmailForPublicFailure(state, _action: PayloadAction<ApiExError>) {
      state.preLogin.status = 'error';
      state.preLogin.error = 'An error occurred while fetching the connection type.';
    },
    resetPreLoginStatus(state) {
      state.preLogin.status = null;
      state.preLogin.error = null;
      state.preLogin.connection = null;
      state.preLogin.email = null;
      state.preLogin.phoneNumber = undefined;
    },
    updateAdvisorHasClients(state, { payload }: PayloadAction<boolean>) {
      state.advisorHasClients = payload;
    },
    setOauthRedirectInitiated(state, { payload }: PayloadAction<boolean>) {
      state.oauth.redirectInitiated = payload;
    },
  },
});

let isSessionRefreshDeferred = false;
let pendingSessionRefresh = false;

function invalidateSessionMiddleWareImpl(
  dispatch: Dispatch<AnyAction>,
  _getStore: () => AppState,
  next: Dispatch<AnyAction>,
  action: Action<unknown>,
) {
  next(action);

  if (action.type === 'DEFER_SESSION_REFRESH') {
    isSessionRefreshDeferred = true;
    return;
  }

  if (action.type === 'RESUME_SESSION_REFRESH') {
    isSessionRefreshDeferred = false;

    if (pendingSessionRefresh) {
      setTimeout(() => dispatch(fetchSessionForAuthRequest({ invalidating: true })));
      pendingSessionRefresh = false;
    }

    return;
  }

  if (
    action.type === postAdvisorUpdatesByAdvisorIdResponse.type ||
    action.type === putAdvisorReferralSourceByAdvisorIdResponse.type ||
    action.type === putAdvisorOrganizationByAdvisorIdResponse.type ||
    action.type === deleteOauthConnectionByAdvisorIdResponse.type ||
    action.type === postPartnerLinkRequestUpdateStatusResponse.type ||
    action.type === postAdvisorFieldUpdateByAdvisorIdResponse.type ||
    action.type === postSeatInvitationAcceptBySubscriptionSeatIdResponse.type
  ) {
    if (isSessionRefreshDeferred) {
      pendingSessionRefresh = true;
    } else {
      setTimeout(() => dispatch(fetchSessionForAuthRequest({ invalidating: true })));
    }
  }

  if (action.type === fetchClientsForAdvisorByAdvisorIdResponse.type) {
    const clients = (action as PayloadAction<Client[]>).payload;
    if (Array.isArray(clients)) {
      dispatch(updateAdvisorHasClients(clients.length > 0));
    }
  }
}

export const invalidateSessionMiddleWare = createSimpleMiddleware(invalidateSessionMiddleWareImpl);

export const {
  closeSession,
  fetchSessionForAuthRequest,
  fetchSessionForAuthResponse,
  fetchSessionForAuthFailure,
  storeAuth0Session,
  setClientSessionType,
  putAdvisorConsentByAdvisorIdRequest,
  putAdvisorConsentByAdvisorIdResponse,
  putAdvisorConsentByAdvisorIdFailure,
  putAdvisorConsentsByAdvisorIdRequest,
  putAdvisorConsentsByAdvisorIdResponse,
  putAdvisorConsentsByAdvisorIdFailure,
  postGoogleAuthCodeByAdvisorIdRequest,
  postGoogleAuthCodeByAdvisorIdResponse,
  postGoogleAuthCodeByAdvisorIdFailure,
  deleteOauthConnectionByAdvisorIdRequest,
  deleteOauthConnectionByAdvisorIdResponse,
  deleteOauthConnectionByAdvisorIdFailure,
  postAdvisorOrganizationUpdateByAdvisorIdRequest,
  postAdvisorOrganizationUpdateByAdvisorIdResponse,
  postAdvisorOrganizationUpdateByAdvisorIdFailure,
  postMicrosoftAuthCodeByAdvisorIdRequest,
  postMicrosoftAuthCodeByAdvisorIdResponse,
  postMicrosoftAuthCodeByAdvisorIdFailure,
  fetchAuth0ConnectionTypeByEmailForPublicRequest,
  fetchAuth0ConnectionTypeByEmailForPublicResponse,
  fetchAuth0ConnectionTypeByEmailForPublicFailure,
  resetPreLoginStatus,
  updateAdvisorHasClients,
  setOauthRedirectInitiated,
} = sessionSlice.actions;

export const sessionReducer = sessionSlice.reducer;
